import { useState, useRef } from "react";
import * as XLSX from "xlsx";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableHead, TableRow, Paper, TableContainer,
  Alert, CircularProgress, Chip,
} from "@mui/material";
import { MdCloudUpload, MdDownload } from "react-icons/md";
import useAuthStore from "../../../../store/useAuthStore";

const VALID_UOM = ["piece", "kg", "g", "litre", "ml", "meter", "box", "pack", "set", "ream", "dozen", "unit"];

const TEMPLATE_HEADERS = [
  "name", "description", "category", "unitOfMeasure", "unitCost",
  "currentStock", "minThreshold", "maxThreshold", "reorderPoint",
  "itemCode", "barcode", "location",
];

const downloadTemplate = () => {
  const ws = XLSX.utils.aoa_to_sheet([
    TEMPLATE_HEADERS,
    ["Sample Pen", "Blue ballpoint", "Office Supplies", "piece", 15, 100, 20, 500, 50, "", "", "Shelf A1"],
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "InventoryItems");
  XLSX.writeFile(wb, "inventory_import_template.xlsx");
};

const BulkImportItems = ({ open, onClose, onSuccess }) => {
  const { token } = useAuthStore();
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [step, setStep] = useState("upload"); // upload | preview | confirmCategories | importing | done
  const [unknownCategories, setUnknownCategories] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setRows([]); setFileName(""); setParseError("");
    setStep("upload"); setUnknownCategories([]); setImportResult(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const validateRow = (row) => {
    const name = String(row.name || "").trim();
    const uom = String(row.unitOfMeasure || "piece").trim().toLowerCase();
    const unitCost = parseFloat(String(row.unitCost ?? "").replace(/,/g, ""));
    if (!name) return "Name is required";
    if (!VALID_UOM.includes(uom)) return `Invalid unit "${uom}"`;
    if (isNaN(unitCost) || unitCost < 0) return "unitCost must be non-negative";
    return null;
  };

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name); setParseError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        if (!json.length) { setParseError("Spreadsheet is empty"); return; }
        const parsed = json.map((r, i) => ({
          ...r,
          _rowNum: i + 2,
          _error: validateRow(r),
        }));
        setRows(parsed);
        setStep("preview");
      } catch (err) {
        setParseError("Failed to parse file: " + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const onProceedFromPreview = async () => {
    const validRows = rows.filter((r) => !r._error);
    if (validRows.length === 0) { setParseError("No valid rows to import"); return; }

    const names = [...new Set(validRows.map((r) => String(r.category || "").trim()).filter(Boolean))];
    if (names.length === 0) { runImport(false); return; }

    try {
      const res = await fetch("/api/checkInventoryCategoryNames", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ names }),
      });
      const data = await res.json();
      if (!res.ok) { setParseError(data.error || "Category check failed"); return; }
      if (data.unknown && data.unknown.length > 0) {
        setUnknownCategories(data.unknown);
        setStep("confirmCategories");
      } else {
        runImport(false);
      }
    } catch (err) {
      setParseError("Category check failed: " + err.message);
    }
  };

  const runImport = async (createMissingCategories) => {
    setStep("importing");
    const payload = rows.filter((r) => !r._error).map(({ _rowNum, _error, ...rest }) => rest);
    try {
      const res = await fetch("/api/bulkImportInventoryItems", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rows: payload, createMissingCategories }),
      });
      const data = await res.json();
      if (!res.ok) { setParseError(data.error || "Import failed"); setStep("preview"); return; }
      setImportResult(data);
      setStep("done");
      if (data.createdCount > 0) onSuccess?.();
    } catch (err) {
      setParseError("Import failed: " + err.message);
      setStep("preview");
    }
  };

  const validCount = rows.filter((r) => !r._error).length;
  const invalidCount = rows.length - validCount;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        <span>Bulk Import Inventory Items</span>
        <button onClick={downloadTemplate} className="flex items-center gap-1 text-sm text-primary hover:underline">
          <MdDownload /> Download template
        </button>
      </DialogTitle>
      <DialogContent dividers>
        {parseError && <Alert severity="error" className="mb-3">{parseError}</Alert>}

        {step === "upload" && (
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
            className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer hover:border-primary"
          >
            <MdCloudUpload className="mx-auto text-5xl text-gray-400 mb-2" />
            <p className="text-gray-600">Click or drag an .xlsx file here</p>
            <p className="text-xs text-gray-400 mt-1">First sheet is used. Headers must match the template.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              hidden
              onChange={(e) => handleFile(e.target.files[0])}
            />
          </div>
        )}

        {step === "preview" && (
          <>
            <div className="flex items-center gap-3 mb-3">
              <Chip label={`${fileName}`} size="small" />
              <Chip label={`${validCount} valid`} color="success" size="small" />
              {invalidCount > 0 && <Chip label={`${invalidCount} invalid`} color="error" size="small" />}
            </div>
            <TableContainer component={Paper} sx={{ maxHeight: 380 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>UoM</TableCell>
                    <TableCell>Unit Cost</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r._rowNum} sx={r._error ? { backgroundColor: "#fee2e2" } : {}}>
                      <TableCell>{r._rowNum}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.category || "-"}</TableCell>
                      <TableCell>{r.unitOfMeasure}</TableCell>
                      <TableCell>{r.unitCost}</TableCell>
                      <TableCell>{r.currentStock || 0}</TableCell>
                      <TableCell>
                        {r._error
                          ? <span className="text-red-600 text-xs">{r._error}</span>
                          : <span className="text-green-600 text-xs">OK</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {step === "confirmCategories" && (
          <div>
            <Alert severity="warning" className="mb-3">
              The following categories don't exist yet. Create them automatically?
            </Alert>
            <div className="flex flex-wrap gap-2">
              {unknownCategories.map((c) => <Chip key={c} label={c} />)}
            </div>
          </div>
        )}

        {step === "importing" && (
          <div className="flex items-center justify-center py-10">
            <CircularProgress />
            <span className="ml-3">Importing {validCount} item(s)...</span>
          </div>
        )}

        {step === "done" && importResult && (
          <div>
            <Alert severity={importResult.failedCount === 0 ? "success" : "warning"} className="mb-3">
              {importResult.createdCount} item(s) imported. {importResult.failedCount > 0 && `${importResult.failedCount} failed.`}
            </Alert>
            {importResult.failedCount > 0 && (
              <TableContainer component={Paper} sx={{ maxHeight: 280 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importResult.failed.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell>{f.row}</TableCell>
                        <TableCell>{f.name}</TableCell>
                        <TableCell className="text-red-600 text-xs">{f.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {step === "preview" && (
          <>
            <Button onClick={() => { reset(); }}>Choose Another File</Button>
            <Button variant="contained" disabled={validCount === 0} onClick={onProceedFromPreview}>
              Import {validCount} Item(s)
            </Button>
          </>
        )}
        {step === "confirmCategories" && (
          <>
            <Button onClick={() => setStep("preview")}>Back</Button>
            <Button color="warning" onClick={() => runImport(false)}>
              Skip rows with unknown categories
            </Button>
            <Button variant="contained" onClick={() => runImport(true)}>
              Create categories & import
            </Button>
          </>
        )}
        {(step === "upload" || step === "done") && <Button onClick={handleClose}>Close</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportItems;
